import React, { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowUp, Sparkles } from "lucide-react";
import { useAuth } from "../lib/auth";
import { hasMinRole } from "../lib/rbac";
import type { SystemRole } from "../lib/rbac";
import { cn } from "@/lib/utils";
import { capture } from "@/lib/posthog";
import { useCreateOrchestratorSession } from "../hooks/use-orchestrator";
import { useOrgSuggestions } from "../hooks/use-onboarding";
import { useCanBuildWithAi } from "../hooks/use-can-build-with-ai";
import { useOrgDomain } from "../hooks/use-org-domain";
import { useSetupJourney } from "../hooks/use-setup-journey";
import { OPEN_SETUP_PANEL } from "./onboarding/screens";
import { nameFromDomain } from "./onboarding/product-research";
import { homeTestIds } from "../testids/homeTestIds";
import "./onboarding/onboarding.css";

interface Idea {
  label: string;
  path?: string;
  agentPrompt?: string;
}

/**
 * The ideas under the box: the org's own suggestions for roles that can run
 * them in the agent, and the pages to start from for those who cannot.
 */
function getIdeas(role: SystemRole, canAgent: boolean, personalized: string[]): Idea[] {
  if (canAgent && personalized.length > 0) return personalized.slice(0, 3).map((p) => ({ label: p, agentPrompt: p }));
  const ideas: Idea[] = [];
  if (canAgent) {
    ideas.push({ label: "Recommend a connector for what we are building", agentPrompt: "Recommend a connector for what we are building" });
    ideas.push({ label: "Help me build a workflow", agentPrompt: "Help me build a workflow" });
  } else if (hasMinRole(role, "developer")) {
    ideas.push({ label: "Build a workflow", path: "/integrations?tab=workflows" });
  }
  ideas.push({ label: "Browse connectors", path: "/integrations?tab=connectors" });
  if (hasMinRole(role, "viewer")) ideas.push({ label: "View activity", path: "/activity/events" });
  return ideas.slice(0, 3);
}

/**
 * Home: what the partner's customers should be able to do, in their own
 * words, with ideas written for their product. Sending hands the request to
 * fastn's agent; the partner's AI tool lives in the top bar's Connect AI tools.
 */
export function CommandCenter(): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { userContext } = useAuth();
  const role = (userContext?.role ?? "end_user") as SystemRole;
  const canAgent = useCanBuildWithAi();
  const journey = useSetupJourney({ enabled: hasMinRole(role, "developer") });
  const orgDomain = useOrgDomain();
  const { data: suggestions = [] } = useOrgSuggestions();

  const domain = journey.data?.product?.domain ?? orgDomain.data ?? null;
  const product = journey.data?.product?.name ?? (domain ? nameFromDomain(domain) : userContext?.accountName ?? null);
  const shortName = product ? product.split(/[\s,]/)[0] : null;

  // A build prompt handed over from the setup journey arrives prefilled, and
  // sending it opens the setup panel beside the chat (FirstIntegrationRailPill).
  const navState = location.state as { setupUseCase?: string; focusPrompt?: boolean } | null;
  const setupPrompt = navState?.setupUseCase ?? null;
  // Arriving from "Build your second integration": the box is where the next one starts.
  const focusPrompt = !!navState?.focusPrompt;
  const [query, setQuery] = useState(() => setupPrompt ?? "");
  const createSession = useCreateOrchestratorSession();

  const launch = useCallback(async (text: string, fromSetup = false) => {
    capture("home_prompt_sent", { mode: "fastn" });
    try {
      const session = await createSession.mutateAsync({});
      navigate(`/agent/${session.threadId}`, { state: { initialQuery: text, ...(fromSetup ? { [OPEN_SETUP_PANEL]: true } : {}) } });
    } catch {
      // Session creation failed: stay on Home rather than dead-ending.
    }
  }, [createSession, navigate]);


  const submit = () => {
    const q = query.trim();
    if (!q || createSession.isPending) return;
    setQuery("");
    void launch(q, !!setupPrompt);
  };

  // The integration the setup journey just built is not an idea for the next one.
  const built = journey.data?.status === "completed" ? journey.data.useCase?.trim().toLowerCase() : null;
  const ideas = getIdeas(role, canAgent, suggestions.map((s) => s.prompt).filter((p) => p.trim().toLowerCase() !== built));

  return (
    <div className="ob3 flex-1 flex flex-col min-h-0" data-testid={homeTestIds.commandCenter}>
      <div className="ds [background:transparent]! flex-1! flex! [padding:40px_40px_28px]! min-h-0!">
        <div className="flex-1! flex! flex-col! justify-center! max-w-[640px]! [margin:0_auto]!">
          <h1 className="h1 text-center!" data-testid={homeTestIds.greeting}>
            {shortName ? `What should ${shortName}’s customers be able to do?` : "What should your customers be able to do?"}
          </h1>
          {canAgent && (
            <div className="prompt [margin-top:22px]! [padding:16px]! min-h-[96px]! flex! flex-col! justify-between!" data-testid={homeTestIds.agentHero}>
              <textarea
                className="[background:transparent]! [border:0]! [outline:none]! [resize:none]! t13 [color:var(--fgColor-default)]! placeholder:[color:var(--fgColor-subtle)]! w-full!"
                rows={2}
                autoFocus={focusPrompt}
                placeholder="Describe an integration in plain words…"
                aria-label="Describe an integration in plain words"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); submit(); }
                }}
                data-testid={homeTestIds.agentHeroInput}
              />
              <button
                type="button"
                className={cn("askGo self-end! cursor-pointer!", !query.trim() && "askGo--off")}
                onClick={submit}
                disabled={!query.trim() || createSession.isPending}
                aria-label="Send"
                data-testid={homeTestIds.agentHeroSubmitButton}
              >
                <ArrowUp className="i" aria-hidden="true" />
              </button>
            </div>
          )}
          <div className="lbl [margin-top:22px]!">{product ? `Ideas for ${product}` : "Ideas"}</div>
          <div className="flex! flex-col! gap-[8px]! [margin-top:10px]!" data-testid={homeTestIds.suggestions}>
            {ideas.map((idea, i) => (
              <button
                key={idea.label}
                type="button"
                className="pc [padding:9px_12px]! w-full! text-left! cursor-pointer!"
                onClick={() => (idea.path ? navigate(idea.path) : idea.agentPrompt ? void launch(idea.agentPrompt) : undefined)}
                data-testid={homeTestIds.suggestionPill(i)}
              >
                <Sparkles className="i xs" aria-hidden="true" />
                <span className="flex-1!">{idea.label}</span>
                <ArrowRight className="i xs" aria-hidden="true" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
